import React from 'react';
import { TrackingSessionState } from '@trackthatride/realtime';
import { Ride, Driver } from '@trackthatride/core';

export interface TrackingInfoProps {
  /** Current tracking state */
  state: TrackingSessionState;
  
  /** Custom styling */
  style?: React.CSSProperties;
  
  /** CSS class name */
  className?: string;
  
  /** Show detailed driver information */
  showDriverDetails?: boolean;
  
  /** Show estimated arrival time */
  showETA?: boolean;
  
  /** Show connection status */
  showConnectionStatus?: boolean;
  
  /** Custom formatters */
  formatters?: {
    time?: (timestamp: string) => string;
    status?: (status: string) => string;
    eta?: (eta: string) => string;
  };
}

const defaultTimeFormatter = (timestamp: string): string => {
  try {
    return new Date(timestamp).toLocaleString();
  } catch {
    return 'Unknown';
  }
};

const defaultStatusFormatter = (status: string): string => {
  return status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const defaultETAFormatter = (eta: string): string => {
  try {
    const etaDate = new Date(eta);
    const now = new Date();
    const diffMs = etaDate.getTime() - now.getTime();
    
    if (diffMs <= 0) {
      return 'Arrived';
    }
    
    const diffMinutes = Math.ceil(diffMs / (1000 * 60));
    
    if (diffMinutes < 60) {
      return `${diffMinutes} min`;
    }
    
    const diffHours = Math.floor(diffMinutes / 60);
    const remainingMinutes = diffMinutes % 60;
    
    return `${diffHours}h ${remainingMinutes}m`;
  } catch {
    return 'Unknown';
  }
};

const getStatusColor = (status: string): string => {
  switch (status) {
    case 'pending':
      return '#FFA726'; // Orange
    case 'assigned':
      return '#42A5F5'; // Blue
    case 'in_transit':
      return '#66BB6A'; // Green
    case 'delivered':
      return '#4CAF50'; // Dark Green
    case 'cancelled':
      return '#EF5350'; // Red
    default:
      return '#757575'; // Grey
  }
};

const getConnectionStatusColor = (status: string): string => {
  switch (status) {
    case 'connected':
      return '#4CAF50'; // Green
    case 'connecting':
    case 'reconnecting':
      return '#FFA726'; // Orange
    case 'disconnected':
      return '#EF5350'; // Red
    default:
      return '#757575'; // Grey
  }
};

export const TrackingInfo: React.FC<TrackingInfoProps> = ({
  state,
  style,
  className,
  showDriverDetails = true,
  showETA = true,
  showConnectionStatus = false,
  formatters = {}
}) => {
  const {
    time: formatTime = defaultTimeFormatter,
    status: formatStatus = defaultStatusFormatter,
    eta: formatETA = defaultETAFormatter
  } = formatters;

  const { ride, driver, estimatedArrival, connectionStatus, lastUpdate } = state;

  if (!ride) {
    return (
      <div 
        className={className} 
        style={{ 
          padding: '16px', 
          textAlign: 'center', 
          color: '#757575',
          ...style 
        }}
      >
        No tracking data available
      </div>
    );
  }

  return (
    <div 
      className={className} 
      style={{ 
        padding: '16px', 
        fontFamily: 'system-ui, -apple-system, sans-serif',
        ...style 
      }}
    >
      {/* Connection Status */}
      {showConnectionStatus && (
        <div style={{ marginBottom: '12px', fontSize: '12px' }}>
          <span 
            style={{
              display: 'inline-block',
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: getConnectionStatusColor(connectionStatus),
              marginRight: '6px'
            }}
          />
          Connection: {formatStatus(connectionStatus)}
        </div>
      )}

      {/* Delivery Information */}
      <div style={{ marginBottom: '16px' }}>
        <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600' }}>
          Delivery #{ride.tracking_code}
        </h3>
        
        <div style={{ marginBottom: '8px' }}>
          <strong>Customer:</strong> {ride.customer_name}
        </div>
        
        <div style={{ marginBottom: '8px' }}>
          <strong>Phone:</strong> {ride.phone_number}
        </div>
        
        <div 
          style={{
            display: 'inline-block',
            padding: '4px 8px',
            borderRadius: '4px',
            backgroundColor: getStatusColor(ride.status),
            color: 'white',
            fontSize: '12px',
            fontWeight: '600',
            textTransform: 'uppercase'
          }}
        >
          {formatStatus(ride.status)}
        </div>
      </div>

      {/* Addresses */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ marginBottom: '8px' }}>
          <strong>Pickup:</strong>
          <div style={{ marginLeft: '8px', color: '#666' }}>
            {ride.pickup_address}
          </div>
        </div>
        
        <div style={{ marginBottom: '8px' }}>
          <strong>Delivery:</strong>
          <div style={{ marginLeft: '8px', color: '#666' }}>
            {ride.delivery_address}
          </div>
        </div>
      </div>

      {/* Driver Information */}
      {showDriverDetails && driver && (
        <div style={{ marginBottom: '16px' }}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600' }}>
            Driver Information
          </h4>
          
          <div style={{ marginBottom: '4px' }}>
            <strong>Name:</strong> {driver.firstName} {driver.lastName}
          </div>
          
          {driver.mobileNumber && (
            <div style={{ marginBottom: '4px' }}>
              <strong>Phone:</strong> {driver.mobileNumber}
            </div>
          )}
          
          {driver.vehicleType && (
            <div style={{ marginBottom: '4px' }}>
              <strong>Vehicle:</strong> {driver.vehicleType}
            </div>
          )}
          
          {driver.vehiclePlate && (
            <div style={{ marginBottom: '4px' }}>
              <strong>Plate:</strong> {driver.vehiclePlate}
            </div>
          )}
        </div>
      )}

      {/* ETA */}
      {showETA && estimatedArrival && (
        <div style={{ marginBottom: '16px' }}>
          <strong>Estimated Arrival:</strong>
          <div style={{ 
            marginTop: '4px',
            fontSize: '18px', 
            fontWeight: '600',
            color: '#2196F3'
          }}>
            {formatETA(estimatedArrival)}
          </div>
        </div>
      )}

      {/* Notes */}
      {ride.notes && (
        <div style={{ marginBottom: '16px' }}>
          <strong>Notes:</strong>
          <div style={{ marginTop: '4px', color: '#666' }}>
            {ride.notes}
          </div>
        </div>
      )}

      {/* Last Update */}
      {lastUpdate && (
        <div style={{ 
          fontSize: '12px', 
          color: '#999',
          borderTop: '1px solid #eee',
          paddingTop: '8px'
        }}>
          Last updated: {formatTime(lastUpdate)}
        </div>
      )}
    </div>
  );
};

export default TrackingInfo;